import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';

function StatusIndicator({ status, checkedIn, checkedOut }) {
  if (checkedOut) return <span className="text-xs px-2 py-0.5 bg-green-100 text-green-700 rounded-full font-bold border border-green-200">✅ Complete</span>;
  if (checkedIn) return <span className="text-xs px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full font-bold border border-blue-200">🟢 Active</span>;
  if (status === 'cancelled') return <span className="text-xs px-2 py-0.5 bg-red-100 text-red-700 rounded-full font-bold border border-red-200">🔴 No Show</span>;
  return <span className="text-xs px-2 py-0.5 bg-neutral-100 text-neutral-600 rounded-full font-bold border border-neutral-200">⏳ Pending</span>;
}

export default function ClientPortalPage() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const load = async () => {
      try {
        const res = await axios.get(`/api/portal/${token}`);
        setData(res.data);
      } catch (err) {
        setError(err.response?.data?.error || 'Portal not found or has expired.');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [token]);

  if (loading) return (
    <div className="min-h-screen bg-neutral-50 flex items-center justify-center">
      <div className="w-8 h-8 border-2 border-amber-400 border-t-transparent rounded-full animate-spin" />
    </div>
  );

  if (error) return (
    <div className="min-h-screen bg-neutral-50 flex items-center justify-center p-4">
      <div className="text-center max-w-md">
        <div className="text-6xl mb-4">🔒</div>
        <h2 className="text-xl font-bold text-neutral-900 mb-2">Portal Unavailable</h2>
        <p className="text-neutral-500 mb-4">{error}</p>
        <a href="https://shiftmatch.work" className="text-amber-600 hover:underline text-sm">Visit ShiftMatch →</a>
      </div>
    </div>
  );

  const completedCount = (data.shifts || []).filter(s => s.checked_out_at).length;
  const activeCount = (data.shifts || []).filter(s => s.checked_in_at && !s.checked_out_at).length;

  return (
    <div className="min-h-screen bg-neutral-50">
      {/* Header */}
      <header className="bg-white border-b border-neutral-100 shadow-sm">
        <div className="max-w-3xl mx-auto px-4 py-4 flex items-center justify-between">
          <div>
            <h1 className="font-bold text-neutral-900 text-lg">{data.employer_name}</h1>
            <p className="text-sm text-neutral-500">{data.label}</p>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs text-amber-600 font-semibold bg-amber-50 px-2 py-1 rounded-full border border-amber-200">
              Powered by ShiftMatch
            </span>
          </div>
        </div>
      </header>

      <div className="max-w-3xl mx-auto px-4 py-6">
        {/* Summary */}
        <div className="grid grid-cols-3 gap-3 mb-6">
          <div className="bg-white rounded-xl p-3 text-center border border-neutral-100 shadow-sm">
            <p className="text-2xl font-extrabold text-neutral-900">{(data.shifts || []).length}</p>
            <p className="text-xs text-neutral-400">Total Shifts</p>
          </div>
          <div className="bg-blue-50 rounded-xl p-3 text-center border border-blue-200">
            <p className="text-2xl font-extrabold text-blue-700">{activeCount}</p>
            <p className="text-xs text-blue-500">Active Now</p>
          </div>
          <div className="bg-green-50 rounded-xl p-3 text-center border border-green-200">
            <p className="text-2xl font-extrabold text-green-700">{completedCount}</p>
            <p className="text-xs text-green-500">Completed</p>
          </div>
        </div>

        {/* Shifts */}
        {(data.shifts || []).length === 0 ? (
          <div className="text-center py-12 bg-white rounded-2xl border border-neutral-100 shadow-sm">
            <div className="text-4xl mb-3">📋</div>
            <p className="text-neutral-500">No shifts in this portal yet</p>
          </div>
        ) : (
          <div className="space-y-3 mb-6">
            {data.shifts.map(shift => (
              <div key={shift.id} className="bg-white rounded-2xl border border-neutral-100 shadow-sm p-4">
                <div className="flex items-start justify-between gap-3 mb-3">
                  <div>
                    <h3 className="font-bold text-neutral-900">{shift.title}</h3>
                    <p className="text-sm text-neutral-500">📍 {shift.location}</p>
                  </div>
                  <StatusIndicator
                    status={shift.status}
                    checkedIn={shift.checked_in_at}
                    checkedOut={shift.checked_out_at}
                  />
                </div>

                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs text-neutral-600">
                  <div>
                    <p className="text-neutral-400 font-medium">Date</p>
                    <p className="font-semibold">{shift.date ? new Date(shift.date).toLocaleDateString('en-AU') : 'TBC'}</p>
                  </div>
                  <div>
                    <p className="text-neutral-400 font-medium">Worker</p>
                    <p className="font-semibold">{shift.worker_first_name || '—'}</p>
                  </div>
                  <div>
                    <p className="text-neutral-400 font-medium">Clock In</p>
                    <p className="font-semibold">
                      {shift.checked_in_at
                        ? new Date(shift.checked_in_at).toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit' })
                        : '—'}
                    </p>
                  </div>
                  <div>
                    <p className="text-neutral-400 font-medium">Clock Out</p>
                    <p className="font-semibold">
                      {shift.checked_out_at
                        ? new Date(shift.checked_out_at).toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit' })
                        : shift.checked_in_at ? 'In progress' : '—'}
                    </p>
                  </div>
                </div>

                {shift.hours_worked && (
                  <div className="mt-2 pt-2 border-t border-neutral-50">
                    <span className="text-xs text-brand-600 font-semibold">
                      ⏱ {parseFloat(parseFloat(shift.hours_worked || 0)).toFixed(1)}h worked
                    </span>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Expires notice */}
        {data.expires_at && (
          <p className="text-center text-xs text-neutral-400 mb-4">
            This portal expires {new Date(data.expires_at).toLocaleDateString('en-AU')}
          </p>
        )}
      </div>

      {/* Footer */}
      <footer className="border-t border-neutral-100 bg-white py-4">
        <p className="text-center text-xs text-neutral-400">
          Verify your workforce with{' '}
          <a href="https://shiftmatch.work" className="text-amber-600 hover:underline font-semibold">ShiftMatch</a>
          {' '}| Australia's trusted short-shift platform
        </p>
      </footer>
    </div>
  );
}
